import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {MessageTypesEnum} from '../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {inboxHasColor} from '../../../../../../libs/shared/core/src/models/helpers/inboxHelpers';
import {
  computeIsGlobalNamespace,
  isTeammateNamespace,
} from '../../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  ChromelessButton,
  ChromelessButtonProps,
} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipExplicitCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipExplicitCondition';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {ColorDot} from '../../../../../../libs/shared/ui/src/components/utility/colorDot';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {ConversationInboxesFormatter} from '../../../../../../srcCommon/inboxes/viewer/conversation/conversationInboxesFormatter';
import {PermissionDenied} from '../../../../../../srcCommon/permissions/permissionDenied';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {InboxesFormatter} from '../../../../common/formatters/inboxesFormatter';
import {ConversationsMoveButtonProps} from '../actions/move/conversationsMoveCoordinator';
import {ConversationsMoveCoordinatorContainer} from '../actions/move/conversationsMoveCoordinatorContainer';

/*
 * Props.
 */

interface ConversationTopbarMoveProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
}

/*
 * Style.
 */

const StyledChevronDiv = styled.div`
  grid-area: chevron;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledDotDiv = styled.div`
  grid-area: dot;
  margin-left: 8px;
`;

const StyledNameDiv = styled.div`
  grid-area: name;
  ${ellipsis()};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledButton = styled(ChromelessButton)`
  border-radius: 100px;
  display: grid;

  grid-template-areas: 'dot . name . chevron .';
  grid-template-columns: auto 6px 1fr 2px auto 8px;
  align-items: center;
  height: 20px;
  overflow: hidden;
  max-width: 200px;

  ${(p) => addHoverAndDisabledStyles(p)};
`;

function addHoverAndDisabledStyles(props: ChromelessButtonProps) {
  if (props.isDisabled) {
    return css`
      background-color: ${(p) => p.theme.greys.shade40};
      pointer-events: none;

      ${StyledNameDiv}, ${StyledChevronDiv} {
        color: ${(p) => p.theme.greys.shade70};
      }
    `;
  }

  if (props.hasHover) {
    return css`
      background-color: ${(p) => p.theme.alphas.gray10};
    `;
  }

  return css`
    &:hover {
      background-color: ${(p) => p.theme.alphas.gray10};
    }
  `;
}

const StyledChevronIcon = styled(Icon)`
  fill: none;
`;

/*
 * Component.
 */

export const ConversationTopbarMove: FC<ConversationTopbarMoveProps> = (props) => (
  <ConversationsMoveCoordinatorContainer
    cellsContext={props.cellsContext}
    inboxesParameters={props.inboxesParameters}
    conversations={props.conversations}
    isBulkActionRequired={props.isBulkActionRequired}
    renderButton={(moveProps) => renderButton(moveProps, props)}
    panelId={props.panelId}
  />
);

/*
 * Helpers.
 */

function renderButton(moveProps: ConversationsMoveButtonProps, props: ConversationTopbarMoveProps) {
  const {permissionResult} = moveProps;
  const {cellsContext} = props;
  const areAllInternal = props.conversations.every(
    (c) =>
      c.messageType === MessageTypesEnum.INTERNAL &&
      (isTeammateNamespace(c.namespace) || computeIsGlobalNamespace(c.namespace)),
  );

  return (
    <TooltipCoordinator
      render={() => {
        if (areAllInternal || cellsContext.inDrafts) {
          return null;
        }

        return permissionResult.isAllowed ? (
          renderInboxNameTooltip(moveProps.conversationInboxes)
        ) : (
          <PermissionDenied reason={permissionResult.reason} />
        );
      }}
    >
      <TooltipExplicitCondition isEnabled={!permissionResult.isAllowed} />
      <StyledButton
        isDisabled={!permissionResult.isAllowed}
        ref={moveProps.buttonRef}
        onClick={moveProps.onClick}
        hasHover={moveProps.hasHover}
      >
        {renderInboxName(cellsContext.inDrafts, areAllInternal, moveProps)}
        <StyledChevronDiv>
          <StyledChevronIcon name="chevronDownTiny" />
        </StyledChevronDiv>
      </StyledButton>
    </TooltipCoordinator>
  );
}

function renderInboxName(
  inDrafts: boolean,
  areAllInternal: boolean,
  moveProps: ConversationsMoveButtonProps,
) {
  if (inDrafts) {
    return (
      <>
        <UserThemeConsumer>
          {(theme) => (
            <StyledDotDiv>
              <Icon name="draftFilledSmall" color={theme.greys.shade70} hoverColor={theme.greys.shade70} />
            </StyledDotDiv>
          )}
        </UserThemeConsumer>
        <StyledNameDiv>
          <TooltipOverflowCondition>
            <Trans id="workspace-drafts">Drafts</Trans>
          </TooltipOverflowCondition>
        </StyledNameDiv>
      </>
    );
  }

  if (areAllInternal) {
    return (
      <>
        <UserThemeConsumer>
          {(theme) => (
            <StyledDotDiv>
              <Icon
                name="discussionFilledSmall"
                color={theme.greys.shade70}
                hoverColor={theme.greys.shade70}
              />
            </StyledDotDiv>
          )}
        </UserThemeConsumer>
        <StyledNameDiv>
          <TooltipOverflowCondition>
            <Trans id="workspace-discussions">Discussions</Trans>
          </TooltipOverflowCondition>
        </StyledNameDiv>
      </>
    );
  }

  const isDisabled = !moveProps.permissionResult.isAllowed;

  return (
    <ConversationInboxesFormatter
      conversationInboxes={moveProps.conversationInboxes}
      renderDefault={() => (
        <>
          {renderColorDot(undefined, isDisabled)}
          <StyledNameDiv>
            <TooltipOverflowCondition>
              <Trans id="topbar-teammateinbox">Teammate inbox</Trans>
            </TooltipOverflowCondition>
          </StyledNameDiv>
        </>
      )}
      renderSingleInbox={(singleInbox) => (
        <>
          {renderColorDot(singleInbox, isDisabled)}
          <StyledNameDiv>
            <TooltipOverflowCondition>{singleInbox.name}</TooltipOverflowCondition>
          </StyledNameDiv>
        </>
      )}
      renderManyInboxes={(inboxes) => (
        <>
          {renderColorDot(undefined, isDisabled)}
          <StyledNameDiv>
            <Trans id="topbar-manyinboxes">{inboxes.length} inboxes</Trans>
          </StyledNameDiv>
        </>
      )}
    />
  );
}

function renderInboxNameTooltip(conversationInboxes: ReadonlyArray<InboxModel>) {
  return (
    <ConversationInboxesFormatter
      conversationInboxes={conversationInboxes}
      renderDefault={() => <Trans id="topbar-teammateinbox">Teammate inbox</Trans>}
      renderSingleInbox={(inbox) => inbox.name}
      renderManyInboxes={(inboxes) => (
        <InboxesFormatter
          inboxes={inboxes}
          limit={inboxes.length}
          renderConjunction={(visible, others) => (
            <>
              {visible}
              {', '}
              {others}
            </>
          )}
        />
      )}
    />
  );
}

function renderColorDot(inbox: InboxModel | undefined, isDisabled: boolean) {
  const inboxColor = (inbox && inboxHasColor(inbox) && inbox.color) || undefined;

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledDotDiv>
          <ColorDot
            color={inboxColor || (isDisabled ? theme.greys.shade60 : theme.greys.shade50)}
            hoverColor={inboxColor || theme.alphas.gray20}
            size={8}
          />
        </StyledDotDiv>
      )}
    </UserThemeConsumer>
  );
}
